import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import type { StudentAccount, StudentWorkspace } from '@/entities/student-workspace'
import { activeFieldsFromWorkspace, createEmptyWorkspace, createStudentId, normalizeWorkspace } from '@/store/workspace'
import type { WorkspaceCommitCoordinator } from '@/repositories/workspace'

type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']
type CreateStudentInput = Parameters<AppState['createStudent']>[0]

interface StudentSliceOptions {
  commitCoordinator?: WorkspaceCommitCoordinator
}

const clearLearnerTransientAnalysis = {
  aiTeacherActive: false,
  phaseAdvancement: null,
} as const

export function createStudentsSlice(set: SetState, _get: GetState, options: StudentSliceOptions = {}): Pick<AppState,
  'createStudent' | 'createStudentAndCommit' | 'selectStudent' | 'deleteStudent' | 'replaceStudentData'> {
  const createStudentInMemory = ({ name, grade, avatar = '🦊' }: CreateStudentInput) => {
    const id = createStudentId()
    const now = new Date().toISOString()
    const account: StudentAccount = {
      id, name: name.trim() || '小探险家', grade, avatar,
      hasCompletedPretest: false, createdAt: now, updatedAt: now,
    }
    set((state) => {
      const workspace = createEmptyWorkspace(account)
      return {
        activeStudentId: id,
        studentIndex: [...state.studentIndex, account],
        workspaces: { ...state.workspaces, [id]: workspace },
        ...activeFieldsFromWorkspace(workspace),
        ...clearLearnerTransientAnalysis,
      }
    })
    return id
  }

  return {
    createStudent: createStudentInMemory,
    createStudentAndCommit: async (input) => {
      const previous = _get()
      const id = createStudentInMemory(input)
      if (!options.commitCoordinator) return id
      try {
        await options.commitCoordinator.whenIdle()
        return id
      } catch (error) {
        options.commitCoordinator.discardNextWrite()
        set(previous)
        throw error
      }
    },
    selectStudent: (studentId) => set((state) => {
      const account = state.studentIndex.find((item) => item.id === studentId)
      if (!account) return state
      const workspace = state.workspaces[studentId] ?? createEmptyWorkspace(account)
      return { activeStudentId: studentId, ...activeFieldsFromWorkspace(workspace), ...clearLearnerTransientAnalysis }
    }),
    deleteStudent: (studentId) => set((state) => {
      const studentIndex = state.studentIndex.filter((item) => item.id !== studentId)
      const workspaces = { ...state.workspaces }
      delete workspaces[studentId]
      if (state.activeStudentId !== studentId) return { studentIndex, workspaces }
      const empty = createEmptyWorkspace({ name: '小探险家', grade: 'G3', avatar: '🦊' })
      return { studentIndex, workspaces, activeStudentId: null, ...activeFieldsFromWorkspace(empty), ...clearLearnerTransientAnalysis, currentGrade: 'all' }
    }),
    replaceStudentData: (backup) => set(() => {
      const studentIndex = backup.studentIndex.map((student) => ({ ...student }))
      const workspaces = Object.fromEntries(
        studentIndex.map((account) => [account.id, normalizeWorkspace(account, backup.workspaces[account.id])]),
      ) as Record<string, StudentWorkspace>
      const activeStudentId = backup.activeStudentId && workspaces[backup.activeStudentId]
        ? backup.activeStudentId : studentIndex[0]?.id ?? null
      if (!activeStudentId) {
        const empty = createEmptyWorkspace({ name: '小探险家', grade: 'G3', avatar: '🦊' })
        return { studentIndex: [], workspaces: {}, activeStudentId: null, ...activeFieldsFromWorkspace(empty), ...clearLearnerTransientAnalysis, currentGrade: 'all' }
      }
      return { studentIndex, workspaces, activeStudentId, ...activeFieldsFromWorkspace(workspaces[activeStudentId]), ...clearLearnerTransientAnalysis }
    }),
  }
}
